<template>
  <button
      :class="buttonClass"
      @click="handleExport"
      :disabled="isExporting || disabled"
      :title="tooltip"
  >
    <!-- Loading State -->
    <template v-if="isExporting">
      <b-spinner small></b-spinner> {{ loadingText }}
    </template>
    
    <!-- Normal State -->
    <template v-else>
      <i :class="iconClass"></i> {{ buttonText }}
    </template>
  </button>
</template>

<script>
import axios from 'axios';
import Swal from "sweetalert2";

export default {
  name: 'ExcelExport',
  props: {
    // Core export configuration
    endpoint: {
      type: String,
      required: true,
      validator: (value) => value && value.trim().length > 0
    },
    filename: {
      type: String,
      default: 'export.xlsx',
      validator: (value) => value && (value.endsWith('.xlsx') || value.endsWith('.xls'))
    },
    payload: {
      type: Object,
      default: () => ({})
    },
    
    // Button appearance
    buttonText: {
      type: String,
      default: 'Export Excel'
    },
    buttonVariant: {
      type: String,
      default: 'info',
      validator: (value) => ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark'].includes(value)
    },
    buttonSize: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value)
    },
    outline: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    },
    
    // Icon configuration
    iconClass: {
      type: String,
      default: 'fas fa-download'
    },
    
    // Loading state
    loadingText: {
      type: String,
      default: 'Exporting...'
    },
    
    // Behavior
    disabled: {
      type: Boolean,
      default: false
    },
    tooltip: {
      type: String,
      default: ''
    },
    
    // Advanced options
    requestTimeout: {
      type: Number,
      default: 300000 // 5 minutes
    },
    retryAttempts: {
      type: Number,
      default: 1,
      validator: (value) => value >= 1 && value <= 5
    },
    
    // Custom headers
    headers: {
      type: Object,
      default: () => ({})
    }
  },
  
  data() {
    return {
      isExporting: false,
      currentAttempt: 0
    };
  },
  
  computed: {
    buttonClass() {
      const baseClass = 'btn';
      const variant = this.outline ? `btn-outline-${this.buttonVariant}` : `btn-${this.buttonVariant}`;
      const size = this.buttonSize !== 'md' ? `btn-${this.buttonSize}` : '';
      const block = this.block ? 'btn-block w-100' : '';
      
      return [baseClass, variant, size, block].filter(Boolean).join(' ');
    }
  },
  
  methods: {
    async handleExport() {
      if (this.isExporting || this.disabled) return;
      
      this.currentAttempt = 0;
      await this.attemptExport();
    },
    
    async attemptExport() {
      this.currentAttempt++;
      this.isExporting = true;

      try {
        this.$emit('export-start', {
          endpoint: this.endpoint,
          filename: this.filename,
          payload: this.payload,
          attempt: this.currentAttempt
        });

        const config = {
          method: 'POST',
          url: this.endpoint,
          data: this.payload,
          timeout: this.requestTimeout,
          responseType: 'arraybuffer',
          headers: {
            'Content-Type': 'application/json',
            ...this.headers
          }
        };

        const response = await axios(config);

        const contentType = response.headers['content-type'];

        // 🧠 CASE 1: JSON response → Queued export
        if (contentType && contentType.includes('application/json')) {
          const textData = new TextDecoder().decode(response.data);
          const jsonData = JSON.parse(textData);

          this.$emit('export-success', jsonData);

          // ✅ SweetAlert2 version
          Swal.fire({
            icon: 'info',
            title: 'Export Queued',
            text: jsonData.message || 'Your export has been queued and will be emailed once ready.',
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'OK',
          });

          return;
        }

        // 🧠 CASE 2: Excel binary → Download immediately
        if (response.status === 200) {
          await this.downloadFile(response.data);
          this.$emit('export-success', {
            filename: this.filename,
            size: response.data.byteLength,
            attempt: this.currentAttempt
          });
        } else {
          throw new Error(`Server returned status ${response.status}`);
        }

      } catch (error) {
        if (this.currentAttempt < this.retryAttempts && this.isRetryableError(error)) {
          await this.delay(2000);
          await this.attemptExport();
          return;
        }

        this.$emit('export-error', {
          error: error.message,
          status: error.response?.status,
          attempt: this.currentAttempt
        });

        this.$bvToast?.toast('Export failed. Please try again.', {
          title: 'Export Error',
          variant: 'danger',
          solid: true
        });

      } finally {
        this.isExporting = false;
      }
    },

    async downloadFile(data) {
      try {
        // Create blob and download
        const blob = new Blob([data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.filename);
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
      } catch (error) {
        throw new Error('Failed to download file: ' + error.message);
      }
    },
    
    isRetryableError(error) {
      // Network errors, timeouts, and 5xx server errors are retryable
      return (
        error.code === 'ECONNABORTED' ||
        error.code === 'NETWORK_ERROR' ||
        !error.response ||
        (error.response.status >= 500 && error.response.status < 600)
      );
    },
    
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }
  }
};
</script>

<style scoped>
.btn {
  transition: all 0.2s ease-in-out;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.btn:active:not(:disabled) {
  transform: translateY(0);
}
</style>